<template>
  <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 py-8 md:py-10 px-4 xl:px-0">
    <!-- Left Content: Artist Profile.
         On desktop this box is exactly as tall as the sidebar and scrolls inside itself. -->
    <div class="order-1 md:order-1 md:col-span-8 relative md:min-h-[560px]">

      <div
        ref="profileScroll"
        @scroll="onProfileScroll"
        class="card p-6 sm:p-8 md:p-10 space-y-10 gsap-artist-detail md:absolute md:inset-0 md:overflow-y-auto scroll-thin"
      >
        <!-- Top Section -->
        <div class="flex flex-col md:flex-row md:items-center gap-6 md:gap-10 gsap-header">
          <div class="md:flex-1 space-y-2 text-left">
            <span v-if="artist.is_renowned" class="badge bg-brand-cream/70 text-brand-red-dark">
              <i class="fa-solid fa-star text-[10px] text-amber-500"></i> Renowned Artist · স্বনামধন‍্য শিল্পী
            </span>
            <h1 class="font-serif text-3xl md:text-4xl font-bold text-brand-red leading-tight">{{ artist.name }}</h1>
            <h2 v-if="artist.profession" class="text-lg font-medium text-gray-500">{{ artist.profession }}</h2>
            <p v-if="artist.quote" class="!mt-4 border-l-4 border-brand-cream-dark pl-4 font-serif text-lg italic text-gray-700">"{{ artist.quote }}"</p>
            <p v-if="artist.dob" class="!mt-4 text-sm text-gray-500 flex items-center gap-2">
              <i class="fa-regular fa-calendar text-gray-400"></i>
              Born: {{ new Date(artist.dob).toLocaleDateString() }}
            </p>
          </div>

          <div class="md:w-[260px] flex-shrink-0">
            <img
              :src="artist.image"
              :alt="artist.name"
              class="w-full aspect-[4/3] md:aspect-square object-cover rounded-md shadow-md ring-4 ring-white bg-gray-100 gsap-image"
            />
          </div>
        </div>

        <!-- Description -->
        <div class="gsap-desc pt-8 border-t border-gray-100">
          <p class="prose-text">
            {{ artist.description }}
          </p>
        </div>

        <!-- Achievements -->
        <div v-if="artist.achievements">
          <div class="section-heading mb-4">
            <h3 class="section-title">Achievements</h3>
          </div>
          <ul class="space-y-2.5 text-gray-700 text-base leading-relaxed">
            <li v-for="(achievement, index) in formatAchievements(artist.achievements)" :key="index" class="flex gap-3">
              <i class="fa-solid fa-award text-brand-red mt-1.5 text-sm"></i>
              <span>{{ achievement }}</span>
            </li>
          </ul>
        </div>

        <!-- Gallery -->
        <section class="gsap-gallery">
          <div class="section-heading mb-4">
            <h2 class="section-title">Gallery</h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <img
              v-for="(galleryImage, index) in artist.galleryImages"
              :key="index"
              :src="galleryImage"
              :alt="`Gallery ${index + 1}`"
              class="w-full h-[220px] object-cover rounded-md shadow-sm bg-gray-100"
            />
          </div>
        </section>

        <!-- Social Links (Moved) -->
        <div v-if="artist.links && artist.links.length" class="pt-8 border-t border-gray-100">
          <div class="section-heading mb-4">
            <h3 class="section-title">Connect</h3>
          </div>
          <div class="flex flex-wrap gap-3">
            <a
              v-for="(link, index) in artist.links"
              :key="index"
              :href="ensureUrl(link.link_url)"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center px-5 py-2 bg-white text-gray-700 rounded-md shadow-sm hover:shadow-md hover:border-brand-red hover:text-brand-red transition-all border border-gray-200 group"
            >
              <span class="font-medium">{{ link.link_type }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square ml-2 text-xs text-brand-red"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- "More below" fade, hidden once the box is scrolled to the end -->
      <div
        v-show="canScrollMore"
        class="pointer-events-none absolute inset-x-px bottom-px hidden h-20 rounded-b-md bg-gradient-to-t from-white via-white/80 to-transparent md:flex items-end justify-center pb-2"
      >
        <span class="text-xs font-medium text-gray-400"><i class="fa-solid fa-angles-down mr-1"></i>Scroll for more</span>
      </div>
    </div>

    <!-- Right Sidebar -->
    <div class="order-3 md:order-2 md:col-span-4">
      <RightSidebar />
    </div>

    <!-- Comments & Likes: its own card, always visible below the profile -->
    <div class="order-2 md:order-3 md:col-span-12">
      <div v-reveal class="card p-6 sm:p-8 md:p-10">
          <div class="flex items-center justify-between mb-6">
            <div class="section-heading">
              <h3 class="section-title">Comments & Likes</h3>
            </div>

            <!-- Like Button -->
            <button
              @click="toggleLike"
              class="flex items-center gap-2 px-4 py-2 rounded-md border transition-all duration-300 hover:scale-105"
              :class="userLiked ? 'bg-red-50 border-red-100 text-brand-red' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :fill="userLiked ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span class="font-bold tabular-nums">{{ likesCount }}</span>
            </button>
          </div>

          <!-- Comments List -->
          <div class="space-y-3 mb-8 max-h-[400px] overflow-y-auto pr-1">
            <div v-if="comments.length === 0" class="text-center py-8 text-gray-500 bg-gray-50 rounded-md border border-dashed border-gray-200">
              No comments yet. Be the first to share your thoughts!
            </div>

            <div v-for="comment in comments" :key="comment.id" class="bg-gray-50 p-4 rounded-md border border-gray-100">
              <div class="flex justify-between items-start mb-2">
                <span class="font-semibold text-gray-900">{{ comment.user_name || 'Anonymous' }}</span>
                <span class="text-xs text-gray-400">{{ new Date(comment.created_at).toLocaleDateString() }}</span>
              </div>
              <p class="text-gray-700 text-sm whitespace-pre-wrap">{{ comment.comment }}</p>
            </div>
          </div>

          <!-- Add Comment Form -->
          <div v-if="currentUser">
            <h4 class="form-label">Leave a comment</h4>
            <div class="flex gap-2">
              <textarea
                v-model="newComment"
                rows="2"
                placeholder="Write your appreciation here..."
                class="form-input resize-none text-sm"
              ></textarea>
              <button
                @click="submitComment"
                :disabled="!newComment.trim() || isSubmitting"
                class="btn-primary self-end rounded-md"
              >
                Send
              </button>
            </div>
          </div>
          <div v-else class="text-center py-4 bg-gray-50 rounded-md text-gray-600">
            <router-link to="/login" class="text-brand-red font-semibold hover:underline">Log in</router-link> to like and comment.
          </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import Swal from 'sweetalert2';
import { authHeaders } from '@/api';
import RightSidebar from "../components/RightsideBar.vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

// ENV - keep your existing defaults
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || "https://sanskritirdapoon.in/api";
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL || "https://sanskritirdapoon.in";

// reactive artist state
const artist = ref({
  name: "",
  profession: "",
  dob: "",
  quote: "",
  description: "",
  achievements: "",
  image: `${FILE_BASE_URL}/images/default.png`,
  galleryImages: [],
  links: [],
});

// social state
const likesCount = ref(0);
const userLiked = ref(false);
const comments = ref([]);
const newComment = ref("");
const currentUser = ref(null);
const isSubmitting = ref(false);

// route
const route = useRoute();

// parse achievements
function formatAchievements(achievements) {
  if (!achievements) return [];
  return achievements
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// helper to ensure valid url
function ensureUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

// fetch artist by id
async function fetchArtist() {
  const id = route.params.id;
  if (!id) return;

  // reset
  artist.value = {
    name: "",
    profession: "",
    description: "",
    achievements: "",
    image: `${FILE_BASE_URL}/images/default.png`,
    galleryImages: [],
    links: [],
  };

  // scroll to top
  window.scrollTo(0, 0);

  try {
    // Token lets owners/admins preview a profile that is still pending approval
    const res = await fetch(`${API_BASE_URL}/artists/${id}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    artist.value = {
      ...data,
      image: data.image
        ? (data.image.startsWith("data:") || data.image.startsWith("http") ? data.image : `${FILE_BASE_URL}${data.image}`)
        : `${FILE_BASE_URL}/images/default.png`,
      galleryImages:
        data.galleryImages && data.galleryImages.length
          ? data.galleryImages.map(img => (img.startsWith("data:") || img.startsWith("http") ? img : `${FILE_BASE_URL}${img}`))
          : [],
    };

    // wait DOM update then animate
    await nextTick();
    animateDetail();
    if (profileScroll.value) profileScroll.value.scrollTop = 0;
    onProfileScroll();
    // images change the height once they load
    setTimeout(onProfileScroll, 800);
    
    // Fetch social data after artist is loaded
    fetchSocialData(id);
    
  } catch (err) {
    console.error("Error fetching artist:", err);
  }
}

// fetch social data
async function fetchSocialData(artistId) {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    currentUser.value = user;
    const userIdQuery = user ? `?user_id=${user.id}` : '';
    
    const res = await fetch(`${API_BASE_URL}/artists/${artistId}/social${userIdQuery}`);
    if (res.ok) {
      const data = await res.json();
      likesCount.value = data.likes;
      userLiked.value = data.user_liked;
      comments.value = data.comments;
    }
  } catch (err) {
    console.error("Error fetching social data:", err);
  }
}

// toggle like
async function toggleLike() {
  if (!currentUser.value) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'Please log in to like this artist.',
      confirmButtonColor: '#BF3131'
    });
    return;
  }
  
  const originalLiked = userLiked.value;
  const originalCount = likesCount.value;
  
  // Optimistic UI update
  userLiked.value = !userLiked.value;
  likesCount.value += userLiked.value ? 1 : -1;
  
  try {
    const res = await fetch(`${API_BASE_URL}/artists/${artist.value.id}/like`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({}) // Backend uses token for user_id
    });
    
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to toggle like");
    }
    
  } catch (err) {
    // Revert on error
    userLiked.value = originalLiked;
    likesCount.value = originalCount;
    Swal.fire({
      icon: 'error',
      title: 'Action Failed',
      text: 'Action failed. Please try again.',
      confirmButtonColor: '#BF3131'
    });
  }
}

// submit comment
async function submitComment() {
  if (!currentUser.value || !newComment.value.trim()) return;
  
  isSubmitting.value = true;
  try {
    const res = await fetch(`${API_BASE_URL}/artists/${artist.value.id}/comments`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({ 
        comment: newComment.value
      })
    });
    
    if (res.ok) {
      // Refresh comments
      fetchSocialData(artist.value.id);
      newComment.value = "";
    } else {
      const errorData = await res.json();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorData.error || 'Failed to post comment.',
        confirmButtonColor: '#BF3131'
      });
    }
  } catch (err) {
    console.error("Error posting comment:", err);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Error posting comment.',
      confirmButtonColor: '#BF3131'
    });
  } finally {
    isSubmitting.value = false;
  }
}

// GSAP animation - kills previous triggers to avoid duplication
let gsapCtx;
// GSAP animation - reverts only this page's previous animations to avoid duplication
function animateDetail() {
  // Killing every ScrollTrigger would leave other v-reveal elements stuck invisible
  if (gsapCtx) gsapCtx.revert();
  gsapCtx = gsap.context(() => {
    // overall fade-in
    gsap.from(".gsap-artist-detail", { opacity: 0, y: 30, duration: 0.6, ease: "power2.out" });

    // header elements
    gsap.from(".gsap-header", { opacity: 0, y: 12, duration: 0.6, delay: 0.1, stagger: 0.08 });

    // image pop
    gsap.from(".gsap-image", { opacity: 0, scale: 0.98, duration: 0.7, delay: 0.15 });

    // Description and gallery live inside the profile's own scroll box on desktop,
    // so they animate on load rather than on page scroll (page scroll never reaches them)
    gsap.from(".gsap-desc", { opacity: 0, y: 20, duration: 0.8, delay: 0.25 });
    gsap.from(".gsap-gallery img", { opacity: 0, y: 30, duration: 0.8, delay: 0.4, stagger: 0.12 });
  });
}

// "Scroll for more" hint on the desktop profile box
const profileScroll = ref(null);
const canScrollMore = ref(false);
function onProfileScroll() {
  const el = profileScroll.value;
  if (!el) return;
  canScrollMore.value = el.scrollHeight - el.scrollTop - el.clientHeight > 8;
}
let resizeObserver;
onMounted(() => {
  // Box height follows the sidebar (ads load later), so re-check whenever it resizes
  if (typeof ResizeObserver !== "undefined" && profileScroll.value) {
    resizeObserver = new ResizeObserver(onProfileScroll);
    resizeObserver.observe(profileScroll.value);
  }
});
onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

onBeforeUnmount(() => {
  if (gsapCtx) gsapCtx.revert();
});

// initial mount
onMounted(() => {
  fetchArtist();
});

// watch route param id changes and re-fetch
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchArtist();
    }
  }
);
</script>